import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardList, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { PartnerSubmission } from "@/app/pages/partner/PartnerSubmission";
import { ApplicationCommissionCard } from "@/app/pages/partner/ApplicationCommissionCard";

const OUTCOMES = [["offer", "Offer"], ["conditional_offer", "Conditional offer"], ["accepted", "Accepted"], ["declined", "Declined"]];
const input = "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15";

export default function PartnerApplicationDetail() {
  const { applicationId } = useParams();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [infoType, setInfoType] = useState("information");
  const [statusNote, setStatusNote] = useState("");
  const [pickStatus, setPickStatus] = useState("");
  const [outcome, setOutcome] = useState("");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/partner/applications/${applicationId}`).then((response) => setDetail(response.data))
    .catch((requestError) => setError(formatApiErrorDetail(requestError.response?.data?.detail) || "Application not found"));
  useEffect(() => { load(); }, [applicationId]);

  const act = async (path, body, successMessage) => {
    setBusy(true);
    try {
      await api.post(`/partner/applications/${applicationId}/${path}`, body);
      toast.success(successMessage);
      setInfoMessage(""); setStatusNote(""); setOutcome(""); setOutcomeNote(""); setPickStatus("");
      await load();
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="rounded-2xl bg-rose-50 p-4 text-sm font-semibold text-rose-700" data-testid="partner-application-error">{error}</p>;
  if (!detail) return <p className="text-sm text-slatey">Loading…</p>;

  return (
    <div data-testid="partner-application-detail" className="space-y-6">
      <Link to="/app/partner/applications" className="inline-flex items-center gap-2 text-sm font-bold text-slatey hover:text-cobalt"><ArrowLeft className="h-4 w-4" /> All applications</Link>

      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink" data-testid="partner-applicant-name">{detail.applicant}</h1>
            <p className="mt-1 text-sm text-slatey">{detail.programme} · {detail.provider} · {detail.intake}</p>
            <p className="mt-1 text-xs text-slatey">{detail.reference} · via {detail.partner_name}</p>
          </div>
          <span className="rounded-full bg-cobalt/10 px-4 py-2 text-sm font-bold capitalize text-cobalt" data-testid="partner-application-status">{detail.status.replaceAll("_", " ")}</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-white p-5">
            <h2 className="font-display text-xl font-bold text-ink">Applicant information</h2>
            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
              <div><dt className="text-xs font-bold text-slatey">Email</dt><dd className="font-semibold text-ink">{detail.applicant_email || "—"}</dd></div>
              <div><dt className="text-xs font-bold text-slatey">Location</dt><dd className="font-semibold text-ink">{detail.applicant_location || "—"}</dd></div>
              <div><dt className="text-xs font-bold text-slatey">English status</dt><dd className="font-semibold capitalize text-ink">{(detail.english || "not specified").replaceAll("_", " ")}</dd></div>
              <div><dt className="text-xs font-bold text-slatey">Eligibility</dt><dd className="font-semibold text-ink">{detail.eligibility?.headline || "No linked assessment"}</dd></div>
            </dl>
            {detail.education_records?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold text-slatey">Education</p>
                <ul className="mt-2 space-y-1 text-sm text-ink">
                  {detail.education_records.map((record, index) => <li key={index} className="rounded-xl bg-surface px-3 py-2">{record.qualification || record.title || "Education record"} · {record.institution || ""}</li>)}
                </ul>
              </div>
            )}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5">
            <h2 className="font-display text-xl font-bold text-ink">Documents</h2>
            {detail.documents.length ? (
              <ul className="mt-3 space-y-2">
                {detail.documents.map((doc, index) => (
                  <li key={doc.id || doc.label || index} className="flex items-center justify-between rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`partner-doc-${doc.id || doc.label || index}`}>
                    <span className="font-semibold text-ink">{doc.label || doc.document_type}</span>
                    <span className="text-xs text-slatey">{doc.document_type} · {doc.uploaded_at?.slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-3 text-sm text-slatey">No documents attached yet.</p>}
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5">
            <h2 className="font-display text-xl font-bold text-ink">Timeline</h2>
            <ol className="mt-3 space-y-3">
              {detail.timeline.map((event, index) => (
                <li key={event.id || index} className="flex gap-3 text-sm" data-testid={`partner-timeline-${index}`}>
                  <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-cobalt" />
                  <div>
                    <p className="font-semibold text-ink">{event.title}</p>
                    {event.description && <p className="text-xs leading-5 text-slatey">{event.description}</p>}
                    <p className="text-xs text-slatey">{event.created_at?.slice(0, 16).replace("T", " ")}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <aside className="space-y-5">
          <ApplicationCommissionCard applicationId={applicationId} />

          <PartnerSubmission applicationId={applicationId} applicationStatus={detail.status} onChanged={load} />

          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="partner-actions">
            <h2 className="font-display text-xl font-bold text-ink">Actions</h2>

            <p className="mt-4 text-xs font-bold uppercase text-slatey">Request more information</p>
            <select value={infoType} onChange={(e) => setInfoType(e.target.value)} data-testid="request-info-type" className={`${input} mt-2`}>
              <option value="information">Information</option>
              <option value="documents">Documents</option>
            </select>
            <textarea value={infoMessage} onChange={(e) => setInfoMessage(e.target.value)} rows={3} data-testid="request-info-message" className={`${input} mt-2`} placeholder="Tell the learner exactly what you need…" />
            <button onClick={() => act("request-info", { request_type: infoType, message: infoMessage }, "Request sent to the learner.")} disabled={busy || infoMessage.trim().length < 5}
              data-testid="request-info-submit" className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} <ClipboardList className="h-4 w-4" /> Send request
            </button>

            <p className="mt-6 text-xs font-bold uppercase text-slatey">Update status</p>
            <select value={pickStatus} onChange={(e) => setPickStatus(e.target.value)} data-testid="partner-status-select" className={`${input} mt-2`}>
              <option value="">Choose status…</option>
              {(detail.allowed_statuses || []).map((status) => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}
            </select>
            <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} data-testid="partner-status-note" className={`${input} mt-2`} placeholder="Note (optional)" />
            <button onClick={() => act("status", { status: pickStatus, note: statusNote || undefined }, "Status updated.")} disabled={busy || !pickStatus}
              data-testid="partner-status-submit" className="mt-2 w-full rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-ink disabled:opacity-50">Update status</button>

            <p className="mt-6 text-xs font-bold uppercase text-slatey">Record decision / outcome</p>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="partner-outcome-select" className={`${input} mt-2`}>
              <option value="">Choose outcome…</option>
              {OUTCOMES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <input value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} data-testid="partner-outcome-note" className={`${input} mt-2`} placeholder="Decision note (optional)" />
            <button onClick={() => act("outcome", { outcome, note: outcomeNote || undefined }, "Outcome recorded.")} disabled={busy || !outcome}
              data-testid="partner-outcome-submit" className="mt-2 w-full rounded-full bg-emerald-600 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Record outcome</button>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5">
            <h2 className="font-display text-xl font-bold text-ink">Information requests</h2>
            {(detail.info_requests || []).length ? (
              <ul className="mt-3 space-y-3">
                {detail.info_requests.map((request, index) => (
                  <li key={request.id || index} className="rounded-2xl bg-surface p-3 text-sm" data-testid={`info-request-${request.id || index}`}>
                    <p className="text-xs font-bold capitalize text-slatey">{request.request_type} · {request.status}</p>
                    <p className="mt-1 text-ink">{request.message}</p>
                    {request.response_message && <p className="mt-2 rounded-xl bg-white p-2 text-xs text-ink"><strong>Response:</strong> {request.response_message}</p>}
                  </li>
                ))}
              </ul>
            ) : <p className="mt-3 text-sm text-slatey">No requests yet.</p>}
          </section>
        </aside>
      </div>
    </div>
  );
}
